import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// Define the merchandise options
interface MerchOption {
  id: string;
  name: string;
  description: string;
  exclusivity: "all" | "revolution" | "none";
  forSale: boolean;
  imageUrl?: string;
}

const merchandiseOptions: MerchOption[] = [
  {
    id: "tshirt",
    name: "Movement T-Shirt",
    description: "Premium organic cotton t-shirt with the cheqbooq emblem.",
    exclusivity: "all",
    forSale: true,
  },
  {
    id: "limited-hoodie",
    name: "Limited Edition Hoodie",
    description: "Exclusive premium hoodie only available to Revolution Partners.",
    exclusivity: "revolution",
    forSale: false,
  },
  {
    id: "metal-card",
    name: "Metal Membership Card",
    description: "Numbered metal member card with your name engraved.",
    exclusivity: "revolution",
    forSale: false,
  }
];

const MerchandiseSection = () => {
  const forSaleItems = merchandiseOptions.filter(item => item.forSale);
  const exclusiveItems = merchandiseOptions.filter(item => !item.forSale);

  return (
    <section className="py-20 px-6 bg-secondary/10">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="premium-title mb-3">Spread the Movement</h2>
        <p className="premium-body max-w-2xl mx-auto mb-10">
          Every supporter receives exclusive merchandise. Display your commitment to financial 
          data ownership with our carefully designed collection.
        </p>
        
        {/* For Sale Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-display font-medium mb-6">Available for Purchase</h3>
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-6 mb-8 max-w-lg mx-auto">
            <p className="text-accent font-medium">Coming Soon</p>
            <p className="text-muted-foreground text-sm mt-2">Merchandise will be available soon</p>
          </div>
          <div className="flex justify-center">
            <div className="grid grid-cols-1 gap-6">
              {forSaleItems.map((item) => (
                <MerchandiseCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </div>

        {/* Exclusive Items Section */}
        <div className="mb-10">
          <h3 className="text-2xl font-display font-medium mb-6">Revolution Partner Exclusives</h3>
          <p className="text-muted-foreground mb-6 text-sm">
            These exclusive items are reserved for Revolution Partners and are not available for purchase.
          </p>
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {exclusiveItems.map((item) => (
                <MerchandiseCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </div>
        
        <p className="text-xs text-muted-foreground mt-6">
          You'll be contacted to confirm your shipping details when available.
        </p>
      </div>
    </section>
  );
};

const MerchandiseCard = ({ item }: { item: MerchOption }) => {
  return (
    <Card className="overflow-hidden flex flex-col h-full max-w-sm">
      <div className="bg-secondary/30 h-40 flex items-center justify-center">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} className="object-contain h-full w-full" />
        ) : (
          <div className="text-4xl font-display text-primary/20">cheqbooq</div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        {!item.forSale && (
          <div className="flex justify-center mb-2">
            <Badge variant="outline" className="bg-primary/10 border-primary/20 text-primary">Exclusive</Badge>
          </div>
        )}
        <h3 className="text-lg font-medium text-center mb-2">{item.name}</h3>
        <p className="text-sm text-muted-foreground text-center">{item.description}</p>
      </div>
    </Card>
  );
};

export default MerchandiseSection;
